<template>
  <div>
    <h3>All Workloads</h3>
    <span>This is a list of all faculty who require a workload. Their latest
      workload information is included.</span>

    <b-form inline @submit.prevent="" class="mb-2">
      <b-input-group class="filter-group">
        <b-input-group-prepend is-text>
          <font-awesome-icon icon="search" />
        </b-input-group-prepend>
        <b-form-input @input="searchItems" v-model="filters.search" placeholder="Type to Search"></b-form-input>
        <b-input-group-append>
          <b-button @click="clearFilter()">Clear</b-button>
        </b-input-group-append>
      </b-input-group>

      <b-form-select v-model="filters.timePeriodId" :options="timePeriods" value-field="timePeriodId"
        text-field="description" @input="filterItems()" class="mb-2 mx-2">
        <template slot="first">
          <!-- this slot appears above the options from 'options' prop -->
          <option :value="null">All Years</option>
        </template>
      </b-form-select>

      <b-input-group class="filter-group">
        <b-input-group-prepend is-text> Subject codes </b-input-group-prepend>
        <b-form-input @input="searchItems" v-model="filters.subjectCode" placeholder="ex: EXW, KIN, NTR"></b-form-input>
      </b-input-group>

      <b-input-group class="filter-group">
        <b-form-checkbox @input="filterItems" v-model="filters.chsOnly" class="font-weight-bold">CHS
          Only</b-form-checkbox>
      </b-input-group>
    </b-form>

    <b-table striped hover no-provider-filtering no-provider-paging no-provider-sorting no-local-sorting
      :items="workloadFaculty" :fields="fields" @sort-changed="changeSort" responsive>
      <template v-slot:cell(name)="data">
        {{ data.item.lastName }}, {{ data.item.firstName }}
      </template>

      <template v-slot:cell(jobcodeDescr)="data">
        <!-- {{ getPrimaryEmployeeJob(data.item.employeeJobs) ? getPrimaryEmployeeJob(data.item.employeeJobs).job.description : "" }} -->
        <div v-if="
          data.item.latestWorkload && data.item.latestWorkload.employeeJobs
        ">
          <div v-for="(job, index) in data.item.latestWorkload.employeeJobs" :key="index">
            {{ job.job.description }} - {{ job.department.description }}
            <span v-if="job.jobIndicator == 'P'">(Primary)</span>
          </div>
        </div>
        <div v-else>
          <div v-for="(job, index) in data.item.employeeJobs" :key="index">
            {{ job.job.description }} - {{ job.department.description }}
            <span v-if="job.jobIndicator == 'P'">(Primary)</span>
          </div>
        </div>
      </template>

      <template v-for="workloadTypeDefinition in workloadTypeDefinitions"
        v-slot:[dynamicCellName(workloadTypeDefinition.name)]="data">
        {{
          data.item.latestWorkload &&
            getWorkloadType(
              data.item.latestWorkload.workloadTypes,
              workloadTypeDefinition.workloadTypeDefinitionId
            )
            ? getWorkloadType(
              data.item.latestWorkload.workloadTypes,
              workloadTypeDefinition.workloadTypeDefinitionId
            ).value
            : null | percentage(0)
        }}
      </template>

      <template v-slot:cell(status)="data">
        {{
          data.item.latestWorkload
            ? data.item.latestWorkload.currentStatus.workloadStatusDefinition
              .name
            : null
        }}
      </template>
      <template v-slot:cell(timePeriod)="data">
        {{
          data.item.latestWorkload
            ? data.item.latestWorkload.timePeriod.description
            : null
        }}
      </template>
      <template v-slot:cell(details)="data">
        <div v-if="data.item.latestWorkload">
          <router-link :to="{
            name: 'workload',
            params: { workloadId: data.item.latestWorkload.workloadId },
          }">Details</router-link>
        </div>
        <span v-if="isWorkloadsManager">
          |
          <router-link :to="{
            name: 'workloadAddBehalf',
            params: { asurite: data.item.asuriteId },
            query: { timePeriod: currentTimePeriod.timePeriodId },
          }">Submit {{ currentTimePeriod.description }}</router-link>
          |
          <router-link :to="{
            name: 'workloadAddBehalf',
            params: { asurite: data.item.asuriteId },
            query: { timePeriod: nextTimePeriod.timePeriodId },
          }">Submit {{ nextTimePeriod.description }}</router-link>
        </span>
      </template>
      <template v-slot:cell(buyoutRequests)="data">
        <div v-if="data.item.latestWorkload">
          <div v-for="request in data.item.latestWorkload.buyoutRequests" :key="request.buyoutRequestId">
            Status: {{ request.currentStatus.statusName }} -
            {{ request.numberOfClasses }} classes
          </div>
        </div>
      </template>
    </b-table>

    <b-pagination :total-rows="totalRows" v-model="currentPage" :per-page="perPage" align="right"
      @change="changePage" />
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex"
import employeesApi from "../../api/employees"
import roleChecks from "../../misc/role-checks"
import debounce from "lodash/debounce"

export default {
  mixins: [roleChecks],
  data() {
    return {
      workloadFaculty: [],
      currentPage: 1,
      perPage: 20,
      sortBy: "name",
      sortDesc: false,
      totalRows: 0,

      filters: {
        search: "",
        timePeriodId: null,
        chsOnly: true,
        subjectCode: ""
      }
    }
  },

  computed: {
    ...mapState({
      timePeriods: state => state.timePeriods.timePeriods,
      currentTimePeriod: state => state.status.currentTimePeriod,
      nextTimePeriod: state => state.status.nextTimePeriod,
      workloadTypeDefinitions: state => state.workloads.workloadTypeDefinitions,
      fiscalYearTimePeriod: state => state.status.fiscalYearTimePeriod
    }),

    fields: function () {
      var tableFields = []

      if (this.showDiscrepancyCol) {
        tableFields.push({ key: "discrepancy", label: "" })
      }

      tableFields.push({ key: "name", sortable: true })
      tableFields.push({ key: "asuriteId", sortable: true })
      tableFields.push({ key: "jobcodeDescr", sortable: true, label: "Jobs" })
      tableFields.push({ key: "status", sortable: true })
      tableFields.push({ key: "timePeriod", sortable: true })
      //  tableFields.push({ key: "notes", sortable: true });

      _.forEach(this.workloadTypeDefinitions, function (workloadTypeDefinition) {
        var newField = {
          key: workloadTypeDefinition.name,
          label: workloadTypeDefinition.name + " (%)",
          sortable: true
        }

        tableFields.push(newField)
      })

      tableFields.push({ key: "buyoutRequests" })

      tableFields.push({ key: "details", label: "" })

      return tableFields
    }
  },

  methods: {
    //...mapActions("employees", ["getWorkloadFaculty"]),

    ...mapActions("timePeriods", ["getTimePeriods"]),

    ...mapActions("status", ["getNextTimePeriod", "getCurrentTimePeriod"]),

    ...mapActions("workloads", ["getWorkloadTypeDefinitions"]),

    ...mapActions("status", [
      "getFiscalYearTimePeriod",
      "getNextTimePeriod",
      "getCurrentTimePeriod"
    ]),

    dynamicCellName(name) {
      return "cell(" + name + ")"
    },

    getWorkloadType(workloadTypes, workloadTypeDefinitionId) {
      return _.find(workloadTypes, function (workloadType) {
        return (
          workloadType.workloadTypeDefinitionId === workloadTypeDefinitionId
        )
      })
    },

    clearFilter: function () {
      this.filters.search = ""
      this.updateItems()
      //localStorage.removeItem("filter");
    },
    changeSort(context) {
      this.sortBy = context.sortBy
      this.sortDesc = context.sortDesc
      this.updateItems()
    },
    changePage(page) {
      this.currentPage = page
      this.updateItems()
    },
    filterItems() {
      this.currentPage = 1
      this.awards = []
      this.updateItems()
    },
    searchItems: debounce(function () {
      this.filterItems()
    }, 500),
    updateItems() {
      this.isLoading = true

      employeesApi
        .getWorkloadFaculty(
          this.filters,
          this.currentPage,
          this.perPage,
          this.sortBy,
          this.sortDesc
        )
        .then(response => {
          this.workloadFaculty = response.data
          var meta = JSON.parse(response.headers["x-pagination"])
          this.totalRows = meta.TotalItems
          if (!response.data.length) {
            this.noResults = true
          }
        })
        .finally(() => {
          this.isLoading = false
        })
    }
  },

  created() {
    this.getCurrentTimePeriod()
    this.getNextTimePeriod()
    // this.getTimePeriods();
    this.getWorkloadTypeDefinitions()
    this.searchItems()

    this.getTimePeriods()
  }
}
</script>

<style scoped>
.filter-group {
  margin: 10px 1em 15px 0;
  max-width: 600px;
}
</style>
